//  export const BASE_URL = 'http://localhost:11058/api';
//  const SOCKET_URL = 'ws://localhost:11093';

// All API calls go through the server-side proxy (src/app/api/proxy/[...path]/route.js).
// The proxy injects the Basic credential from server-side env vars, so no
// credentials ever reach the client bundle.
 export const BASE_URL = '/api/proxy';
 export const SOCKET_URL = '/ws';
// // demo//
// const BASE_URL = 'https://emerald-system.id:11061/api';
// const SOCKET_URL = 'wss://emerald-system.id:11095';

import { isValidDateString } from '@/utils/dateUtils';

// Room availability lock/unlock endpoints
const LOCK_ROOM_URL = `${BASE_URL}/rooms/availability/lock`;
const UNLOCK_ROOM_URL = `${BASE_URL}/rooms/availability/unlock`;

// Helper: check if a promotion is currently valid for DISPLAY
// Rules:
//   - booking_start / booking_end  => when guest must MAKE the booking
//   - stay_end                     => hide expired promos whose stay window has ended
//   - usage_limit / total_usage    => total quota check
export const isPromotionValid = (promo) => {
  if (!promo) {
    console.log('[isPromotionValid] rejected: promo is null/undefined');
    return false;
  }

  // Must be active: legacy 'active' field (1/'1') OR status === 'active'
  const isActiveLegacy = promo.active === 1 || promo.active === '1';
  const isActiveStatus = promo.status === 'active';
  if (!isActiveLegacy && !isActiveStatus) {
    console.log(`[isPromotionValid] ${promo.promo_code} rejected: not active (active=${promo.active}, status=${promo.status})`);
    return false;
  }

  // Must have a promo code
  if (!promo.promo_code || String(promo.promo_code).trim() === '') {
    console.log('[isPromotionValid] rejected: no promo_code');
    return false;
  }

  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  // Check BOOKING period (when guest makes the reservation)
  // Compare as local date strings (YYYY-MM-DD) to avoid timezone issues
  const bookingStartValid = isValidDateString(promo.booking_start);
  const bookingEndValid = isValidDateString(promo.booking_end);

  if (bookingStartValid && bookingEndValid) {
    if (today < promo.booking_start || today > promo.booking_end) {
      console.log(`[isPromotionValid] ${promo.promo_code} rejected: today=${today} outside booking period ${promo.booking_start} - ${promo.booking_end}`);
      return false;
    }
  } else if (bookingStartValid) {
    if (today < promo.booking_start) {
      console.log(`[isPromotionValid] ${promo.promo_code} rejected: today=${today} before booking_start ${promo.booking_start}`);
      return false;
    }
  } else if (bookingEndValid) {
    if (today > promo.booking_end) {
      console.log(`[isPromotionValid] ${promo.promo_code} rejected: today=${today} after booking_end ${promo.booking_end}`);
      return false;
    }
  }

  // Check STAY period - hide promos whose stay window has already ended
  const stayEndValid = isValidDateString(promo.stay_end);
  if (stayEndValid) {
    if (today > promo.stay_end) {
      console.log(`[isPromotionValid] ${promo.promo_code} rejected: today=${today} after stay_end ${promo.stay_end}`);
      return false;
    }
  }

  // Check usage limit
  if (promo.usage_limit !== undefined && promo.usage_limit !== null &&
      promo.total_usage !== undefined && promo.total_usage !== null) {
    const limit = Number(promo.usage_limit);
    const used = Number(promo.total_usage);
    if (!isNaN(limit) && !isNaN(used) && used >= limit) {
      console.log(`[isPromotionValid] ${promo.promo_code} rejected: usage ${used}/${limit} exceeded`);
      return false;
    }
  }

  console.log(`[isPromotionValid] ${promo.promo_code} accepted`);
  return true;
};

// Special URL for OTP operations (goes through the server-side proxy)
const OTP_BASE_URL = '/api/proxy';

// Add flexible check-in configuration
const CHECKIN_CONFIG = {
  // Flexible check-in settings
  flexible_checkin: {
    enabled: false,
    weekday_only: false,  // Only allow flexible check-in on weekdays
    excluded_rate_codes: ['WEEKEND', 'HOLIDAY'], // Rate codes that don't allow flexible check-in
    start_time: '14:00',  // Default check-in time
    end_time: '18:00',    // Latest check-in time
    excluded_dates: [], // Special dates where flexible check-in is not available
    // Function to check if flexible check-in is available
    isAvailable: (checkInDate, rateCode) => {
      try {
        // Validate checkInDate
        if (!checkInDate) return false;
        
        const date = new Date(checkInDate);
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
          console.warn('Invalid date in isAvailable:', checkInDate);
          return false;
        }
        
        const day = date.getDay();
        
        // Check if it's a weekday (0 = Sunday, 6 = Saturday)
        const isWeekday = day !== 0 && day !== 6;
        
        // Check if rate code allows flexible check-in
        const isAllowedRate = !rateCode || !CHECKIN_CONFIG.flexible_checkin.excluded_rate_codes.includes(rateCode);
        
        // Check if date is not in excluded dates - use safe date format conversion
        const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        const isNotExcluded = !CHECKIN_CONFIG.flexible_checkin.excluded_dates.includes(dateString);
        
        return CHECKIN_CONFIG.flexible_checkin.enabled && 
               CHECKIN_CONFIG.flexible_checkin.weekday_only && 
               isWeekday && 
               isAllowedRate && 
               isNotExcluded;
      } catch (error) {
        console.error('Error in isAvailable function:', error);
        return false;
      }
    }
  }
};

// Add API configuration
const API_CONFIG = {
  credentials: 'omit',
  mode: 'cors',
  headers: {
    'Content-Type': 'application/json'
  }
};

const multiproperty = false;
const propertycode = [

  {
    code: 'SWH',
    description: 'SWH'
  }
];

// WebSocket configuration
let ws = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY = 3000;

export const initializeSocket = () => {
  if (!ws || ws.readyState === WebSocket.CLOSED) {
    ws = new WebSocket(SOCKET_URL);

    ws.onopen = () => {
      console.log('WebSocket connected successfully');
      reconnectAttempts = 0;
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
        console.log(`Attempting to reconnect... (${reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS})`);
        setTimeout(() => {
          reconnectAttempts++;
          initializeSocket();
        }, RECONNECT_DELAY);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'visitorUpdate') {
          // Dispatch to all registered callbacks
          callbacks.forEach(callback => callback(data));
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };
  }
  return ws;
};

// Store callbacks for visitor updates
const callbacks = new Set();

// Track visitors on a specific page
export const trackVisitor = (page) => {
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    ws = initializeSocket();
  }

  if (!page) {
    page = 'home';
  }

  let endpoint = 'home';
  if (page.includes('booking') || page.includes('book-now')) {
    endpoint = 'rooms';
  } else if (page.includes('reservation')) {
    endpoint = 'reservations';
  } else if (page.includes('payment')) {
    endpoint = 'payments';
  } else if (page.includes('hotel')) {
    endpoint = 'hotels';
  }

  console.log('Tracking visitor on endpoint:', endpoint);
  if (ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({
      type: 'trackVisitor',
      endpoint
    }));
  } else {
    ws.addEventListener('open', () => {
      ws.send(JSON.stringify({
        type: 'trackVisitor',
        endpoint
      }));
    }, { once: true });
  }
};

// Get visitor count updates
export const onVisitorUpdate = (callback) => {
  if (!ws || ws.readyState !== WebSocket.OPEN) {
    ws = initializeSocket();
  }

  callbacks.add(callback);

  return () => {
    callbacks.delete(callback);
  };
};

// Cleanup WebSocket connection
export const cleanupSocket = () => {
  if (ws) {
    ws.close();
    ws = null;
    callbacks.clear();
  }
};

// Get headers for API requests
// NOTE: No Authorization header is sent from the client. The server-side proxy
// (src/app/api/proxy/[...path]/route.js) injects the credential.
export const getHeaders = () => {
  const headers = new Headers();

  // Add content type header
  headers.append('Content-Type', 'application/json');
  
  // Update last activity when making API calls
  if (typeof window !== 'undefined') {
    try {
      const { updateLastActivity } = require('@/utils/sessionManager');
      updateLastActivity();
    } catch (error) {
      // Silently handle if sessionManager is not available
    }
  }
  
  // Try to get saved property from localStorage
  let database = 'SWH'; // Default to SWH property code

  if (typeof window !== 'undefined') {
    try {
      console.log('Attempting to get property from localStorage for API headers');
      const savedProperty = localStorage.getItem('selectedProperty');
      if (savedProperty) {
        try {
          const property = JSON.parse(savedProperty);
          if (property && property.code) {
            // Pastikan hanya menggunakan satu nilai database (tidak ada koma)
            database = property.code.split(',')[0].trim();
            console.log('Using property code from localStorage for API headers:', database);
          } else {
            console.warn('Invalid property in localStorage, using default:', database);
          }
        } catch (parseError) {
          console.error('Error parsing property from localStorage:', parseError);
          console.warn('Using default property code:', database);
        }
      } else {
        console.log('No property found in localStorage, using default for API headers:', database);
      }
    } catch (e) {
      console.warn('Error reading selected property:', e.message);
      console.warn('Using default property code:', database);
    }
  }

  // Add X-Database header which is explicitly allowed
  headers.append('X-Database', database);

  // Log headers for debugging
  console.log('Generated headers with X-Database:', database);

  return headers;
};

// Get headers specifically for JSON POST requests
export const getJsonHeaders = () => {
  return getHeaders(); // Already includes Content-Type: application/json
};

// Utility function for making API requests with timeout and error handling
export const makeApiRequest = async (url, options = {}, timeoutMs = 8000, retries = 0) => {
  let currentRetry = 0;
  
  const executeRequest = async () => {
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      // Get headers with current selected property
      const headers = options.headers || getHeaders();

      // Add signal and CORS config to options
      const requestOptions = {
        credentials: 'omit',
        mode: 'cors',
        ...options,
        headers: headers,
        signal: controller.signal
      };

      // Log request details for debugging
      console.log('Making API request:', {
        url,
        method: requestOptions.method || 'GET',
        headers: Object.fromEntries(headers.entries()),
        retry: currentRetry > 0 ? `${currentRetry}/${retries}` : 'initial'
      });

      // Make the request
      const response = await fetch(url, requestOptions);
      clearTimeout(timeoutId);

      // Log response status
      console.log('API response status:', response.status);

      // Check for HTTP errors
      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || `Request failed with status ${response.status}`;
        } catch {
          errorMessage = `Request failed with status ${response.status}`;
        }

        // NOTE: A 401/403 here means the BACKEND rejected the proxy's shared
        // Basic credential (API_BASIC_AUTH) or the X-Database value — it is NOT
        // a user session expiry. Do NOT redirect/clear session here; that turns
        // a backend outage into a redirect loop. Just return the error and let
        // each caller fall back gracefully. Real idle-timeout expiry is handled
        // separately by initializeSessionMonitoring() in sessionManager.
        if (response.status === 401 || response.status === 403) {
          console.warn(
            `Backend rejected request (${response.status}): ${errorMessage} — ` +
            `check server-side API_BASIC_AUTH / X-Database, not user session.`
          );
          return {
            status: 'error',
            message: errorMessage,
            data: []
          };
        }

        console.warn('API error response:', errorMessage);
        return {
          status: 'error',
          message: errorMessage,
          data: []
        };
      }

      // Parse JSON response
      const result = await response.json();
      return result;
    } catch (error) {
      clearTimeout(timeoutId);

      // Handle timeout errors
      if (error.name === 'AbortError') {
        console.warn(`Request timed out (retry ${currentRetry}/${retries})`);
        
        // If we have retries left, try again
        if (currentRetry < retries) {
          currentRetry++;
          console.log(`Retrying request (${currentRetry}/${retries})...`);
          // Add exponential backoff delay
          const backoffDelay = Math.min(1000 * Math.pow(2, currentRetry), 10000);
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
          return executeRequest();
        }
        
        return {
          status: 'error',
          message: 'Request timed out after all retry attempts',
          data: []
        };
      }

      // Handle network errors
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        console.error('Network error - Failed to fetch:', {
          url,
          error: error.message,
          retry: `${currentRetry}/${retries}`
        });
        
        // If we have retries left, try again
        if (currentRetry < retries) {
          currentRetry++;
          console.log(`Retrying request (${currentRetry}/${retries})...`);
          // Add exponential backoff delay
          const backoffDelay = Math.min(1000 * Math.pow(2, currentRetry), 10000);
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
          return executeRequest();
        }
        
        return {
          status: 'error',
          message: 'Unable to connect to the server after multiple attempts. Please check your connection.',
          data: []
        };
      }

      // Handle other errors
      console.error('API request error:', {
        url,
        error: error.message,
        stack: error.stack,
        retry: `${currentRetry}/${retries}`
      });

      // If we have retries left, try again for other errors too
      if (currentRetry < retries) {
        currentRetry++;
        console.log(`Retrying request (${currentRetry}/${retries})...`);
        // Add exponential backoff delay
        const backoffDelay = Math.min(1000 * Math.pow(2, currentRetry), 10000);
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        return executeRequest();
      }

      return {
        status: 'error',
        message: 'An error occurred while making the request',
        data: []
      };
    }
  };
  
  return executeRequest();
};

// Fetch promotion alerts
export const fetchPromotionAlerts = async (page = 'home', propertyCode = null) => {
  try {
    console.log(`Fetching promotion alerts for page: ${page}, property: ${propertyCode || 'default'}`);

    // Add timestamp to force refresh
    const timestamp = new Date().getTime();
    let url = `${BASE_URL}/alert_promotion?page=${encodeURIComponent(page)}&_t=${timestamp}`;

    const headers = getHeaders();
    // If propertyCode is provided, override the X-Database header
    if (propertyCode) {
      headers.set('X-Database', propertyCode);
    }

    const result = await makeApiRequest(url, {
      method: 'GET',
      headers: headers,
      redirect: 'follow'
    }, 15000, 2); // 15 second timeout for alerts with 2 retries

    // Filter promotions by type
    console.log(`Result: ${JSON.stringify(result)}`);
    if (result.status === 'success' && Array.isArray(result.data)) {
      // For main banner (popup), filter by type 'banner_main' (case insensitive)
      let filteredPromotions = result.data.filter(promo =>
        (promo.active === 1 || promo.status === 'active') &&
        (promo.type?.toLowerCase() === 'banner_main' ||
          promo.type?.toUpperCase() === 'BANNER_MAIN')
      );

      // Also enforce validity (booking period, usage limit, status)
      filteredPromotions = filteredPromotions.filter(isPromotionValid);

      // Add timestamp to image URLs to force refresh
      filteredPromotions = filteredPromotions.map(promo => {
        if (promo.url_promo) {
          promo.url_promo = `${promo.url_promo}?t=${timestamp}`;
        }
        return promo;
      });

      console.log(`Found ${filteredPromotions.length} banner promotions`);

      return {
        ...result,
        data: filteredPromotions
      };
    }

    return result;
  } catch (error) {
    console.error('Error in fetchPromotionAlerts:', error);
    return {
      status: 'error',
      message: error.message,
      data: []
    };
  }
};

// Fetch CTA promotions
export const fetchCTAPromotions = async () => {
  try {
    console.log('Fetching CTA promotions');
    const url = `${BASE_URL}/alert_promotion`;

    const result = await makeApiRequest(url, {
      method: 'GET',
      redirect: 'follow'
    }, 15000, 2); // 15 second timeout for CTAs with 2 retries

    // Filter promotions by type
    if (result.status === 'success' && Array.isArray(result.data)) {
      // For CTA promotions, filter by type 'cta' (case insensitive)
      let filteredPromotions = result.data.filter(promo =>
        (promo.active === 1 || promo.status === 'active') &&
        (promo.type?.toLowerCase() === 'cta' || promo.type?.toUpperCase() === 'CTA')
      );

      // Also enforce validity (booking period, usage limit, status)
      filteredPromotions = filteredPromotions.filter(isPromotionValid);

      console.log(`Found ${filteredPromotions.length} CTA promotions`);

      return {
        ...result,
        data: filteredPromotions
      };
    }

    return result;
  } catch (error) {
    console.error('Error in fetchCTAPromotions:', error);
    return {
      status: 'error',
      message: error.message,
      data: []
    };
  }
};

// Fetch active promotions from promotions API
export const fetchActivePromotions = async (propertyCode = null) => {
  try {
    console.log('Fetching active promotions from promotions API');
    
    // Get the property code from localStorage if not provided
    let dbCode = propertyCode;
    if (!dbCode) {
      try {
        const selectedProperty = typeof window !== 'undefined' 
          ? localStorage.getItem('selectedProperty') 
          : null;
        if (selectedProperty) {
          const property = JSON.parse(selectedProperty);
          dbCode = property.code;
        }
      } catch (error) {
        console.warn('Error getting property code:', error);
      }
    }
    
    // Use DEMO as default if no property code
    const database = dbCode || 'DEMO';
    
    // Fetch all promotions and filter client-side (more reliable than /promotions/active)
    const url = `${BASE_URL}/promotions`;
    
    // Prepare headers with X-Database (proxy injects the credential)
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('X-Database', database);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: headers,
      redirect: 'follow'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    console.log('Promotions API raw response:', result);

    // Filter promotions client-side with our validation
    if (result.status === 'success' && Array.isArray(result.data)) {
      const validPromotions = result.data.filter(isPromotionValid);
      console.log(`[fetchActivePromotions] ${validPromotions.length}/${result.data.length} promos passed client-side filter`);

      return {
        status: 'success',
        data: validPromotions,
        total: validPromotions.length
      };
    }

    return result;
  } catch (error) {
    console.error('Error fetching active promotions:', error);
    return {
      status: 'error',
      message: error.message,
      data: []
    };
  }
};

// Fallback function for older API that doesn't have /promotions/active
const fetchAllPromotionsFallback = async (database) => {
  try {
    const url = `${BASE_URL}/promotions`;
    
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('X-Database', database);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: headers,
      redirect: 'follow'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    // Filter for active promotions
    if (result.status === 'success' && Array.isArray(result.data)) {
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      
      const activePromotions = result.data.filter(promo => {
        const isActive = promo.status === 'active';
        const hasPromoCode = promo.promo_code && promo.promo_code.trim() !== '';

        let isInBookingPeriod = true;
        const bookingStartValid = isValidDateString(promo.booking_start);
        const bookingEndValid = isValidDateString(promo.booking_end);
        if (bookingStartValid && bookingEndValid) {
          isInBookingPeriod = today >= promo.booking_start && today <= promo.booking_end;
        } else if (bookingStartValid) {
          isInBookingPeriod = today >= promo.booking_start;
        } else if (bookingEndValid) {
          isInBookingPeriod = today <= promo.booking_end;
        }

        let hasRemainingUsage = true;
        if (promo.usage_limit && promo.total_usage !== undefined) {
          hasRemainingUsage = promo.total_usage < promo.usage_limit;
        }

        let isInStayPeriod = true;
        const stayEndValid = isValidDateString(promo.stay_end);
        if (stayEndValid) {
          isInStayPeriod = today <= promo.stay_end;
        }

        return isActive && hasPromoCode && isInBookingPeriod && hasRemainingUsage && isInStayPeriod;
      });
      
      return {
        status: 'success',
        data: activePromotions,
        total: activePromotions.length
      };
    }
    
    return result;
  } catch (error) {
    console.error('Error in fallback promotions fetch:', error);
    return {
      status: 'error',
      message: error.message,
      data: []
    };
  }
};

// Fetch hotel theme data
export const fetchHotelTheme = async () => {
  try {
    const myHeaders = getHeaders();

    const response = await fetch(`${BASE_URL}/hotels/1`, {
      method: "GET",
      headers: myHeaders,
      redirect: "follow"
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching hotel theme:', error);
    throw error;
  }
};

// Fungsi khusus untuk kalender - selalu menggunakan roomCount=1 dan default 2 bulan
export const fetchCalendarData = async (checkIn, checkOut, guests, children, roomCount, propertyCode) => {
  try {
    // Require property_code, throw error if missing
    if (!propertyCode) {
      throw new Error('Property code is required for fetching calendar data');
    }
    
    console.log(`Fetching calendar data: ${checkIn} to ${checkOut}, guests: ${guests}, children: ${children}, roomCount: ${roomCount}, property: ${propertyCode}`);

    // Jika tidak ada checkOut atau rentang kurang dari 60 hari, buat rentang 2 bulan
    if (!checkOut) {
      const startDate = checkIn ? new Date(checkIn) : new Date();
      const endDate = new Date(startDate);
      endDate.setMonth(startDate.getMonth() + 2);
      endDate.setDate(0); // Last day of the month

      checkIn = formatDate(startDate);
      checkOut = formatDate(endDate);

      console.log(`Adjusted date range to 2 months: ${checkIn} to ${checkOut}`);
    } else {
      // Periksa apakah rentang kurang dari 60 hari
      const startDate = new Date(checkIn);
      const endDate = new Date(checkOut);
      const dayDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

      if (dayDiff < 60) {
        // Perpanjang rentang hingga 60 hari (sekitar 2 bulan)
        endDate.setMonth(startDate.getMonth() + 2);
        endDate.setDate(0); // Last day of the month
        checkOut = formatDate(endDate);
        console.log(`Extended date range to 2 months: ${checkIn} to ${checkOut}`);
      }
    }

    // First try to get rates from the rates API
    let defaultRates = {};
    try {
      const ratesResponse = await fetchRoomRates(checkIn, checkOut, propertyCode);
      if (ratesResponse.status === 'success' && Array.isArray(ratesResponse.data)) {
        ratesResponse.data.forEach(item => {
          if (item.date && item.base_rate) {
            defaultRates[item.date] = item.base_rate;
          }
        });
      }
    } catch (ratesError) {
      console.warn('Failed to fetch default rates:', ratesError);
      // Use hardcoded default rates if rates API fails
      defaultRates = {
        [checkIn]: 171094,
        [checkOut]: 172788
      };
    }

    // Gunakan BASE_URL yang sudah didefinisikan
    let url = `${BASE_URL}/rooms/available?checkIn=${checkIn}&checkOut=${checkOut}&adults=${guests}&children=${children}&room=1`;

    console.log(`Calendar API URL: ${url}`);

    const headers = getHeaders();
    
    // Override X-Database header with the property code
      headers.set('X-Database', propertyCode);
    console.log(`Setting X-Database header to: ${propertyCode}`);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        redirect: "follow"
      });

      if (!response.ok) {
        console.warn(`Calendar API request failed with status ${response.status}`);
        // Return data with default rates if API fails
        return {
          status: 'success',
          data: [{
            room_info: {
              room_type: 'Standard Room',
              max_occupancy: 2,
              size: '17x12',
              description: 'Standard room with modern amenities',
              images: [],
              url_pic1: '',
              url_pic2: '',
              url_pic3: '',
              url_pic4: '',
              url_pic5: '',
              url_pic6: '',
              facilities: []
            },
            availability: {
              status: 'available',
              dates: Object.entries(defaultRates).map(([date, rate]) => ({
                date,
                final_rate: rate,
                rooms_to_sell: 5,
                include_breakfast: true,
                promo: 0,
                ratedesc: 'room',
                color: '#363738',
                ratecode: 'STD',
                benefits: []
              })),
              rooms_to_sell: 5
            },
            rate_details: {
              color: '#363738',
              benefits: [],
              final_rate: Object.values(defaultRates)[0],
              net_final_rate: Object.values(defaultRates)[0],
              include_breakfast: true
            }
          }]
        };
      }

      const data = await response.json();
      console.log('Calendar API response received');

      // If we have valid data, enhance it with default rates where needed
      if (data && data.status === 'success' && Array.isArray(data.data)) {
        data.data = data.data.map(room => {
          if (room.availability && Array.isArray(room.availability.dates)) {
            room.availability.dates = room.availability.dates.map(dateItem => {
              // If final_rate is null or undefined, use default rate for that date or fallback rate
              if (dateItem.final_rate === null || dateItem.final_rate === undefined) {
                const defaultRate = defaultRates[dateItem.date] || 171094; // Fallback to base rate if no default
                return {
                  ...dateItem,
                  final_rate: defaultRate,
                  net_final_rate: defaultRate,
                  include_breakfast: true,
                  promo: 0,
                  ratedesc: 'room',
                  color: '#363738',
                  ratecode: room.availability.dates[0]?.ratecode || 'STD',
                  benefits: []
                };
              }
              return dateItem;
            });
          }

          // Ensure rate_details has the correct format
          if (!room.rate_details || !room.rate_details.final_rate) {
            const firstDate = room.availability?.dates?.[0];
            room.rate_details = {
              color: firstDate?.color || '#363738',
              benefits: firstDate?.benefits || [],
              final_rate: firstDate?.final_rate || defaultRates[checkIn] || 171094,
              net_final_rate: firstDate?.net_final_rate || defaultRates[checkIn] || 171094,
              include_breakfast: firstDate?.include_breakfast || true
            };
          }

          return room;
        });

        console.log('Enhanced data with default rates where needed');
        return data;
      } else {
        console.warn('Invalid API response format:', data);
        // Return data with default rates if API response is invalid
        return {
          status: 'success',
          data: [{
            room_info: {
              room_type: 'Standard Room',
              max_occupancy: 2,
              size: '17x12',
              description: 'Standard room with modern amenities',
              images: [],
              url_pic1: '',
              url_pic2: '',
              url_pic3: '',
              url_pic4: '',
              url_pic5: '',
              url_pic6: '',
              facilities: []
            },
            availability: {
              status: 'available',
              dates: Object.entries(defaultRates).map(([date, rate]) => ({
                date,
                final_rate: rate,
                net_final_rate: rate,
                rooms_to_sell: 5,
                include_breakfast: true,
                promo: 0,
                ratedesc: 'room',
                color: '#363738',
                ratecode: 'STD',
                benefits: []
              })),
              rooms_to_sell: 5
            },
            rate_details: {
              color: '#363738',
              benefits: [],
              final_rate: Object.values(defaultRates)[0],
              net_final_rate: Object.values(defaultRates)[0],
              include_breakfast: true
            }
          }]
        };
      }
    } catch (fetchError) {
      console.error('Calendar fetch error:', fetchError);
      // Return data with default rates if fetch fails
      return {
        status: 'success',
        data: [{
          room_info: {
            room_type: 'Standard Room',
            max_occupancy: 2,
            size: '17x12',
            description: 'Standard room with modern amenities',
            images: [],
            url_pic1: '',
            url_pic2: '',
            url_pic3: '',
            url_pic4: '',
            url_pic5: '',
            url_pic6: '',
            facilities: []
          },
          availability: {
            status: 'available',
            dates: Object.entries(defaultRates).map(([date, rate]) => ({
              date,
              final_rate: rate,
              net_final_rate: rate,
              rooms_to_sell: 5,
              include_breakfast: true,
              promo: 0,
              ratedesc: 'room',
              color: '#363738',
              ratecode: 'STD',
              benefits: []
            })),
            rooms_to_sell: 5
          },
          rate_details: {
            color: '#363738',
            benefits: [],
            final_rate: Object.values(defaultRates)[0],
            net_final_rate: Object.values(defaultRates)[0],
            include_breakfast: true
          }
        }]
      };
    }
  } catch (error) {
    console.error('Error in fetchCalendarData:', error);
    return {
      status: 'error',
      message: error.message || 'Failed to fetch calendar data',
      data: []
    };
  }
};

// Fungsi reguler untuk fetch available rooms - menggunakan roomCount yang diberikan
export const fetchAvailableRooms = async (checkIn, checkOut, guests, children, roomCount, propertyCode) => {
  try {
    // Require property_code, throw error if missing
    if (!propertyCode) {
      throw new Error('Property code is required for fetching available rooms');
    }
    
    console.log(`Fetching available rooms: ${checkIn} to ${checkOut}, guests: ${guests}, children: ${children}, rooms: ${roomCount}, property: ${propertyCode}`);

    // Add timestamp to prevent caching
    const timestamp = new Date().getTime();

    // Gunakan BASE_URL yang sudah didefinisikan
    let url = `${BASE_URL}/rooms/available?checkIn=${checkIn}&checkOut=${checkOut}&adults=${guests}&children=${children}&room=${roomCount}&_t=${timestamp}`;

    console.log(`API URL: ${url}`);

    const headers = getHeaders();
    
    // Override X-Database header with the property code
      const originalProperty = headers.get('X-Database');
      headers.set('X-Database', propertyCode);
      console.log(`Overriding property code for API call from ${originalProperty} to ${propertyCode}`);

    console.log('Request headers:', Object.fromEntries(headers.entries()));

    const response = await fetch(url, {
      method: 'GET',
      headers: headers,
      redirect: 'follow'
    });

    console.log('Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('API request failed:', errorText);
      throw new Error(`API request failed with status ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log('API response received with property code:', headers.get('X-Database'));

    if (data && data.status === 'success' && Array.isArray(data.data)) {
      return data;
    } else {
      console.warn('Invalid data format from API');
      throw new Error('Invalid data format from API');
    }
  } catch (error) {
    console.error('Error fetching available rooms:', error);
    throw error;
  }
};

// Utility function to clean IP address (remove IPv6 prefix)
const cleanIpAddress = (ip) => {
  if (!ip) return ip;
  
  // Remove IPv6-mapped IPv4 prefix (::ffff:)
  if (ip.startsWith('::ffff:')) {
    return ip.replace('::ffff:', '');
  }
  
  return ip;
};

// Track analytics events
export const trackAnalytics = async (eventData) => {
  try {
    // Validate required fields
    if (!eventData.session_id || !eventData.page || !eventData.event_type) {
      console.warn('⚠️ Analytics: Missing required fields (silently skipping)');
      return { status: 'skipped', message: 'Missing required fields' };
    }

    // Validate event type is one of the allowed values
    const validEventTypes = ['view', 'click', 'submit', 'book', 'cancel'];
    if (!validEventTypes.includes(eventData.event_type)) {
      console.warn('⚠️ Analytics: Invalid event type (silently skipping)');
      return { status: 'skipped', message: 'Invalid event type' };
    }

    // Clean IP address if present in eventData
    if (eventData.ip_address) {
      eventData.ip_address = cleanIpAddress(eventData.ip_address);
    }

    const response = await makeApiRequest(`${BASE_URL}/analytics/track`, {
      method: 'POST',
      headers: getJsonHeaders(),
      body: JSON.stringify(eventData),
    }, 5000, 1); // Short timeout (5s) and only 1 retry for analytics

    // Silently handle analytics errors - don't throw
    if (response.status === 'error') {
      console.warn('⚠️ Analytics tracking failed (non-critical):', response.message);
      return { status: 'skipped', message: 'Analytics tracking failed' };
    }

    return response;
  } catch (error) {
    // Silently catch all analytics errors - they should never break the app
    console.warn('⚠️ Analytics error (non-critical, silently skipped):', error.message);
    return { status: 'skipped', message: 'Analytics tracking skipped due to error' };
  }
};

// Generate a unique session ID if one doesn't exist in localStorage
export const getOrCreateSessionId = () => {
  if (typeof window !== 'undefined') {
    let sessionId = localStorage.getItem('booking_session_id');
    if (!sessionId) {
      sessionId = 'sess_' + Math.random().toString(36).substring(2, 15) + 
                  Math.random().toString(36).substring(2, 15);
      localStorage.setItem('booking_session_id', sessionId);
    }
    return sessionId;
  }
  return null;
};

// Lock room availability when added to cart
export const lockRoomAvailability = async (pmsRoomId, startDate, endDate, propertyCode) => {
  try {
    console.log(`Locking room availability: Room ID ${pmsRoomId} from ${startDate} to ${endDate}`);
    
    // Validate input parameters
    if (!pmsRoomId) {
      throw new Error('Room ID is required for locking availability');
    }
    
    if (!startDate || !endDate) {
      throw new Error('Start date and end date are required for locking availability');
    }
    
    if (!propertyCode) {
      console.warn('Property code not provided for locking room availability');
    }
    
    const headers = getJsonHeaders();
    if (propertyCode) {
      headers.set('X-Database', propertyCode);
    }
    
    const requestBody = {
      pms_room_id: pmsRoomId,
      start_date: startDate,
      end_date: endDate
    };
    
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    try {
      const response = await makeApiRequest(LOCK_ROOM_URL, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      console.log('Lock room response:', response);
      
      // Additional validation of response
      if (!response || response.status !== 'success') {
        console.warn('Room locking returned unsuccessful status:', response?.status);
      }
      
      return response;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError.name === 'AbortError') {
        throw new Error('Room locking request timed out');
      }
      throw fetchError;
    }
  } catch (error) {
    console.error('Error locking room availability:', error);
    return {
      status: 'error',
      message: error.message || 'Failed to lock room availability',
      data: null
    };
  }
};

// Unlock room availability when payment is completed or cart is abandoned
export const unlockRoomAvailability = async (pmsRoomId, startDate, endDate, propertyCode) => {
  try {
    console.log(`Unlocking room availability: Room ID ${pmsRoomId} from ${startDate} to ${endDate}`);
    
    // Validate input parameters
    if (!pmsRoomId) {
      throw new Error('Room ID is required for unlocking availability');
    }
    
    if (!startDate || !endDate) {
      throw new Error('Start date and end date are required for unlocking availability');
    }
    
    if (!propertyCode) {
      console.warn('Property code not provided for unlocking room availability');
    }
    
    const headers = getJsonHeaders();
    if (propertyCode) {
      headers.set('X-Database', propertyCode);
    }
    
    const requestBody = {
      pms_room_id: pmsRoomId,
      start_date: startDate,
      end_date: endDate
    };
    
    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    try {
      const response = await makeApiRequest(UNLOCK_ROOM_URL, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      console.log('Unlock room response:', response);
      
      // Additional validation of response
      if (!response || response.status !== 'success') {
        console.warn('Room unlocking returned unsuccessful status:', response?.status);
      }
      
      return response;
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError.name === 'AbortError') {
        throw new Error('Room unlocking request timed out');
      }
      throw fetchError;
    }
  } catch (error) {
    console.error('Error unlocking room availability:', error);
    return {
      status: 'error',
      message: error.message || 'Failed to unlock room availability',
      data: null
    };
  }
};

// Fungsi untuk mengambil data ketersediaan kamar untuk halaman booking
export const fetchRoomAvailability = async (checkIn, checkOut, adults, children, roomCount, roomType = '', rateCode = '', propertyCode) => {
  try {
    // Require property_code, throw error if missing
    if (!propertyCode) {
      throw new Error('Property code is required for fetching room availability');
    }
    
    // Parse dates if they're in full date string format
    let formattedCheckIn = checkIn;
    let formattedCheckOut = checkOut;

    // Check if the date is in full date string format (contains GMT, etc.)
    if (checkIn.includes('GMT')) {
      formattedCheckIn = formatDate(new Date(checkIn));
    }

    if (checkOut.includes('GMT')) {
      formattedCheckOut = formatDate(new Date(checkOut));
    }

    // Always use the rooms/available endpoint for consistency
    let url = `${BASE_URL}/rooms/available?checkIn=${formattedCheckIn}&checkOut=${formattedCheckOut}&adults=${adults}&children=${children}&room=${roomCount}`;

    // Add roomType and rateCode parameters if provided
    if (roomType) {
      url += `&roomtype=${encodeURIComponent(roomType)}`;
    }

    if (rateCode) {
      url += `&ratecode=${encodeURIComponent(rateCode)}`;
    }

    // Log the property code being used
    console.log(`Room availability API URL: ${url}, property code: ${propertyCode}`);

    const headers = new Headers();
    headers.append("X-Database", propertyCode);
    
    console.log(`Using headers: X-Database: ${propertyCode}`);

    const requestOptions = {
      method: 'GET',
      headers: headers,
      redirect: 'follow'
    };

    // Make the request directly
    const response = await fetch(url, requestOptions);
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    // Parse response
    const responseText = await response.text();
    console.log('Raw API response first 100 chars:', responseText.substring(0, 100) + '...');
    
    const data = JSON.parse(responseText);
    
    // Process response to ensure consistent format with rate information
    if (data.status === 'success' && Array.isArray(data.data)) {
      // Same processing logic as in fetchFilteredRooms
      const processedData = data.data.map(room => {
        const processedRoom = { ...room };
        
        // Ensure availability and dates exist
        if (!processedRoom.availability) {
          processedRoom.availability = { status: 'available', dates: [], rooms_to_sell: 0 };
        }
        
        if (!Array.isArray(processedRoom.availability.dates)) {
          processedRoom.availability.dates = [];
        }
        
        // Ensure available_rates exists
        if (!processedRoom.available_rates || !Array.isArray(processedRoom.available_rates) || processedRoom.available_rates.length === 0) {
          // Try to extract available_rates from first date if available
          if (processedRoom.availability.dates.length > 0 && 
              processedRoom.availability.dates[0].available_rates) {
            processedRoom.available_rates = processedRoom.availability.dates[0].available_rates;
          } 
          // Otherwise create available_rates from rate_details or first date info
          else if (processedRoom.availability.dates.length > 0) {
            const firstDate = processedRoom.availability.dates[0];
            processedRoom.available_rates = [{
              color: firstDate.color || '#55cf04',
              ratecode: firstDate.ratecode || 'STD',
              ratedesc: firstDate.ratedesc || room.room_info?.room_type || 'Standard Rate',
              include_breakfast: firstDate.include_breakfast || false,
              final_rate: firstDate.final_rate || 352213,
              net_final_rate: firstDate.net_final_rate || firstDate.final_rate || 352213
            }];
          }
          // If no dates, check rate_details
          else if (processedRoom.rate_details && processedRoom.rate_details.final_rate > 0) {
            processedRoom.available_rates = [{
              color: processedRoom.rate_details.color || '#55cf04',
              ratecode: processedRoom.rate_details.ratecode || 'STD',
              ratedesc: processedRoom.rate_details.ratedesc || room.room_info?.room_type || 'Standard Rate',
              include_breakfast: processedRoom.rate_details.include_breakfast || false,
              final_rate: processedRoom.rate_details.final_rate,
              net_final_rate: processedRoom.rate_details.net_final_rate || processedRoom.rate_details.final_rate
            }];
          } else {
            // Create a default rate with fallback values
            processedRoom.available_rates = [{
              color: '#55cf04',
              ratecode: 'STD',
              ratedesc: room.room_info?.room_type || 'Standard Rate',
              include_breakfast: false,
              final_rate: 352213,
              net_final_rate: 352213
            }];
          }
        }
        
        // Ensure rate_details exists and has valid final_rate
        if (!processedRoom.rate_details || !processedRoom.rate_details.final_rate) {
          if (processedRoom.available_rates && processedRoom.available_rates.length > 0) {
            processedRoom.rate_details = { ...processedRoom.available_rates[0] };
          } else {
            processedRoom.rate_details = {
              color: '#55cf04',
              ratecode: 'STD',
              ratedesc: room.room_info?.room_type || 'Standard Rate',
              include_breakfast: false,
              final_rate: 352213,
              net_final_rate: 352213
            };
          }
        }
        
        return processedRoom;
      });
      
      return {
        ...data,
        data: processedData
      };
    }
    
    return data;
  } catch (error) {
    console.error('Error in fetchRoomAvailability:', error);
    return {
      status: 'error',
      message: error.message || 'Failed to fetch room availability',
      data: []
    };
  }
};

// Fungsi untuk mengambil data kamar dengan filter khusus (roomtype dan ratecode)
export const fetchFilteredRooms = async (checkIn, checkOut, adults, children, roomCount, roomType = '', rateCode = '', propertyCode) => {
  try {
    // Require property_code, throw error if missing
    if (!propertyCode) {
      throw new Error('Property code is required for fetching filtered rooms');
    }
    
    // Parse dates if they're in full date string format
    let formattedCheckIn = checkIn;
    let formattedCheckOut = checkOut;

    // Check if the date is in full date string format (contains GMT, etc.)
    if (checkIn.includes('GMT')) {
      formattedCheckIn = formatDate(new Date(checkIn));
    }

    if (checkOut.includes('GMT')) {
      formattedCheckOut = formatDate(new Date(checkOut));
    }

    // Use the rooms/available endpoint directly, just like the user's example
    let url = `${BASE_URL}/rooms/available?checkIn=${formattedCheckIn}&checkOut=${formattedCheckOut}&adults=${adults}&children=${children}&room=${roomCount}`;

    // Add roomType and rateCode parameters if provided
    if (roomType) {
      url += `&roomtype=${encodeURIComponent(roomType)}`;
    }

    if (rateCode) {
      url += `&ratecode=${encodeURIComponent(rateCode)}`;
    }

    console.log(`Filtered rooms API URL: ${url}, property code: ${propertyCode}`);

    const headers = new Headers();
    headers.append("X-Database", propertyCode);
    
    console.log(`Using headers: X-Database: ${propertyCode}`);

    const requestOptions = {
      method: 'GET',
      headers: headers,
      redirect: 'follow'
    };

    // Make the request using the same pattern as the user's example
    const response = await fetch(url, requestOptions);
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    // Parse response
    const responseText = await response.text();
    console.log('Raw API response first 100 chars:', responseText.substring(0, 100) + '...');
    
    const data = JSON.parse(responseText);
    
    // Process the response to ensure proper rate structure
    if (data.status === 'success' && Array.isArray(data.data)) {
      const processedData = data.data.map(room => {
        // Ensure room has all required properties
        const processedRoom = { ...room };
        
        // Ensure availability and dates exist
        if (!processedRoom.availability) {
          processedRoom.availability = { status: 'available', dates: [], rooms_to_sell: 0 };
        }
        
        if (!Array.isArray(processedRoom.availability.dates)) {
          processedRoom.availability.dates = [];
        }
        
        // Ensure available_rates exists
        if (!processedRoom.available_rates || !Array.isArray(processedRoom.available_rates)) {
          // Try to extract available_rates from first date if available
          if (processedRoom.availability.dates.length > 0 && 
              processedRoom.availability.dates[0].available_rates) {
            processedRoom.available_rates = processedRoom.availability.dates[0].available_rates;
          } 
          // Otherwise create available_rates from rate_details or first date info
          else if (processedRoom.availability.dates.length > 0) {
            const firstDate = processedRoom.availability.dates[0];
            processedRoom.available_rates = [{
              color: firstDate.color || '#55cf04',
              ratecode: firstDate.ratecode || 'STD',
              ratedesc: firstDate.ratedesc || room.room_info?.room_type || 'Standard Rate',
              include_breakfast: firstDate.include_breakfast || false,
              final_rate: firstDate.final_rate || 352213,
              net_final_rate: firstDate.net_final_rate || firstDate.final_rate || 352213
            }];
          }
          // If no dates, check rate_details
          else if (processedRoom.rate_details && processedRoom.rate_details.final_rate > 0) {
            processedRoom.available_rates = [{
              color: processedRoom.rate_details.color || '#55cf04',
              ratecode: processedRoom.rate_details.ratecode || 'STD',
              ratedesc: processedRoom.rate_details.ratedesc || room.room_info?.room_type || 'Standard Rate',
              include_breakfast: processedRoom.rate_details.include_breakfast || false,
              final_rate: processedRoom.rate_details.final_rate,
              net_final_rate: processedRoom.rate_details.net_final_rate || processedRoom.rate_details.final_rate
            }];
          } else {
            // Create a default rate with fallback values
            processedRoom.available_rates = [{
              color: '#55cf04',
              ratecode: 'STD',
              ratedesc: room.room_info?.room_type || 'Standard Rate',
              include_breakfast: false,
              final_rate: 352213,
              net_final_rate: 352213
            }];
          }
        }
        
        // Ensure rate_details exists and has valid final_rate
        if (!processedRoom.rate_details || !processedRoom.rate_details.final_rate) {
          if (processedRoom.available_rates && processedRoom.available_rates.length > 0) {
            processedRoom.rate_details = { ...processedRoom.available_rates[0] };
          } else {
            processedRoom.rate_details = {
              color: '#55cf04',
              ratecode: 'STD',
              ratedesc: room.room_info?.room_type || 'Standard Rate',
              include_breakfast: false,
              final_rate: 352213,
              net_final_rate: 352213
            };
          }
        }
        
        return processedRoom;
      });
      
      return {
        ...data,
        data: processedData
      };
    }

    return data;
  } catch (error) {
    console.error('Error in fetchFilteredRooms:', error);
    return {
      status: 'error',
      message: error.message || 'Failed to fetch filtered rooms',
      data: []
    };
  }
};

// Helper function to format date as YYYY-MM-DD
const formatDate = (date) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

// Enumerate dates from start (inclusive) to end (exclusive) in YYYY-MM-DD
const enumerateDates = (startStr, endStr) => {
  const dates = [];
  const start = new Date(startStr + 'T00:00:00');
  const end = new Date(endStr + 'T00:00:00');
  // Safety guard
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) return dates;
  const cur = new Date(start);
  while (cur < end) {
    dates.push(formatDate(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
};

// Validate current rates for a cart item over a date range using rooms/available
// Returns an object compatible with cart validation UI (success / price_changed / missing_dates)
export const validateCartRates = async ({
  roomType,
  roomId,
  rateCode,
  checkIn,
  checkOut,
  expectedTotalNett,
  propertyCode,
  adults = 2,
  children = 0,
  roomCount = 1
}) => {
  try {
    // Basic param validation
    if (!propertyCode) throw new Error('Property code is required for rate validation');
    if (!checkIn || !checkOut) throw new Error('checkIn and checkOut are required for rate validation');

    const nights = enumerateDates(checkIn, checkOut);
    if (nights.length === 0) {
      return {
        status: 'error',
        code: 'INVALID_RANGE',
        message: 'Invalid date range',
        data: { room_type: roomType, check_in: checkIn, check_out: checkOut }
      };
    }

    // Fetch availability with optional filters
    const response = await fetchRoomAvailability(
      checkIn,
      checkOut,
      adults,
      children,
      roomCount,
      roomType || '',
      rateCode || '',
      propertyCode
    );

    if (!response || response.status !== 'success' || !Array.isArray(response.data)) {
      return {
        status: 'error',
        code: 'FETCH_FAILED',
        message: 'Failed to fetch availability for validation',
        data: null
      };
    }

    // Find matching room
    const matchedRoom = response.data.find(r => {
      const byId = roomId && r.room_info?.room_id === roomId;
      const byType = roomType && r.room_info?.room_type === roomType;
      return byId || byType;
    });

    if (!matchedRoom) {
      return {
        status: 'error',
        code: 'ROOM_NOT_FOUND',
        message: 'Room not found in availability',
        data: { room_type: roomType, room_id: roomId }
      };
    }

    const datesArr = Array.isArray(matchedRoom.availability?.dates) ? matchedRoom.availability.dates : [];
    // Build nightly breakdown by mapping nights to the availability.dates
    const nightly = nights.map(dateStr => {
      // Try to find nightly rate by exact date
      const rec = datesArr.find(d => d.date === dateStr || d.start_date === dateStr);
      const nett = rec?.net_final_rate ?? rec?.final_rate ?? matchedRoom.rate_details?.net_final_rate ?? matchedRoom.rate_details?.final_rate ?? 0;
      const gross = rec?.final_rate ?? matchedRoom.rate_details?.final_rate ?? 0;
      const updatedAt = rec?.updated_at || null;
      return { date: dateStr, nett_amount: Number(nett) || 0, gross_amount: Number(gross) || 0, updated_at: updatedAt };
    });

    const missingDates = nightly.filter(n => !n.nett_amount || n.nett_amount <= 0).map(n => n.date);
    if (missingDates.length > 0) {
      return {
        status: 'error',
        code: 'MISSING_DATES',
        message: 'Rate not found for one or more dates',
        data: { room_type: roomType, check_in: checkIn, check_out: checkOut, missing_dates: missingDates }
      };
    }

    const totalNett = nightly.reduce((acc, r) => acc + (Number(r.nett_amount) || 0), 0);
    // Use latest update timestamp if available on any night, fallback to now
    const serverRateTimestamp = (nightly
      .map(n => n.updated_at)
      .filter(Boolean)
      .sort()
      .pop()) || new Date().toISOString();

    const payload = {
      room_type: roomType || matchedRoom.room_info?.room_type,
      check_in: checkIn,
      check_out: checkOut,
      nights: nights.length,
      nights_breakdown: nightly,
      total_nett: totalNett,
      server_rate_timestamp: serverRateTimestamp
    };

    if (typeof expectedTotalNett === 'number') {
      if (expectedTotalNett !== totalNett) {
        return {
          status: 'error',
          code: 'PRICE_CHANGED',
          message: 'Harga berubah sejak terakhir dilihat',
          data: {
            ...payload,
            is_price_consistent: false,
            price_change_warning: {
              code: 'PRICE_CHANGED',
              expected_total_nett: expectedTotalNett,
              current_total_nett: totalNett,
              diff: totalNett - expectedTotalNett
            }
          }
        };
      }
    }

    return {
      status: 'success',
      message: 'Rates validated for cart item',
      data: {
        ...payload,
        is_price_consistent: true,
        price_change_warning: null
      }
    };
  } catch (error) {
    console.error('Error in validateCartRates:', error);
    return {
      status: 'error',
      code: 'VALIDATION_FAILED',
      message: error.message || 'Rate validation failed',
      data: null
    };
  }
};

export const fetchHotelDetails = async (propertyCode = null) => {
  try {
    // Check for cached hotel details first
    if (typeof window !== 'undefined') {
      // Get the last property code used for caching
      const cachedPropertyCode = sessionStorage.getItem('cachedPropertyCode');
      
      // Clear cache if property code has changed
      if (propertyCode && cachedPropertyCode && cachedPropertyCode !== propertyCode) {
        console.log(`Property code changed from ${cachedPropertyCode} to ${propertyCode}, clearing cache`);
        sessionStorage.removeItem('cachedHotelDetails');
        sessionStorage.removeItem('cachedHotelDetailsTimestamp');
        sessionStorage.removeItem('cachedPromotions');
        sessionStorage.removeItem('cachedPromotionsTimestamp');
        sessionStorage.setItem('cachedPropertyCode', propertyCode);
      } else if (propertyCode && !cachedPropertyCode) {
        // Store current property code
        sessionStorage.setItem('cachedPropertyCode', propertyCode);
      }
      
      // Force clear cache if banner_info fields are missing (for development)
      const cachedData = sessionStorage.getItem('cachedHotelDetails');
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          if (!parsed.hasOwnProperty('banner_info1') || !parsed.hasOwnProperty('banner_info2')) {
            console.log('⚠️ Cache missing banner_info fields, clearing cache...');
            sessionStorage.removeItem('cachedHotelDetails');
            sessionStorage.removeItem('cachedHotelDetailsTimestamp');
          }
        } catch (e) {
          console.warn('Error checking cached data:', e);
        }
      }
      
      // Only use cache if property codes match
      const cachedHotelDetails = sessionStorage.getItem('cachedHotelDetails');
      const cachedTimestamp = sessionStorage.getItem('cachedHotelDetailsTimestamp');
      const now = new Date().getTime();
      const CACHE_DURATION = 60 * 60 * 1000; // 1 hour cache
      
      // Use cached details if available, not expired, and property code matches
      if (cachedHotelDetails && cachedTimestamp && 
          (now - parseInt(cachedTimestamp)) < CACHE_DURATION &&
          (!propertyCode || !cachedPropertyCode || propertyCode === cachedPropertyCode)) {
        try {
          const parsedDetails = JSON.parse(cachedHotelDetails);
          console.log('Using cached hotel details for property:', cachedPropertyCode);
          return parsedDetails;
        } catch (cacheError) {
          console.warn('Error parsing cached hotel details:', cacheError);
          // Continue with API request if cache parsing fails
        }
      }
    }

    const headers = getHeaders();
    
    // If property code is provided, override the X-Database header
    if (propertyCode) {
      headers.set('X-Database', propertyCode);
      console.log(`Setting X-Database header to: ${propertyCode} for hotel details fetch`);
    }

    // Use the correct API URL format for the production environment
    const apiUrl = `${BASE_URL}/hotels/1`;
    console.log(`Fetching hotel details from: ${apiUrl}`);

    // Use makeApiRequest with retry mechanism
    const result = await makeApiRequest(apiUrl, {
      method: 'GET',
      headers: headers,
      redirect: 'follow'
    }, 15000, 3); // 15 second timeout with 3 retries
    
    if (!result || result.status === 'error') {
      throw new Error(result?.message || 'Failed to fetch hotel details');
    }
    
    // Log the response structure to help with debugging
    console.log('Hotel details response structure:', {
      hasData: !!result,
      responseKeys: Object.keys(result).slice(0, 5).join(', ') + (Object.keys(result).length > 5 ? '...' : '')
    });
    
    // Log banner_info fields specifically
    console.log('API Response - banner_info1:', result.banner_info1);
    console.log('API Response - banner_info2:', result.banner_info2);
    
    // Cache the successful result
    if (typeof window !== 'undefined') {
      try {
        const now = new Date().getTime();
        sessionStorage.setItem('cachedHotelDetails', JSON.stringify(result));
        sessionStorage.setItem('cachedHotelDetailsTimestamp', now.toString());
        if (propertyCode) {
          sessionStorage.setItem('cachedPropertyCode', propertyCode);
        }
      } catch (cacheError) {
        console.warn('Error caching hotel details:', cacheError);
      }
    }
    
    return result;
    
  } catch (error) {
    console.error('Error fetching hotel details:', error);
    
    // Try to use cached hotel details as fallback
    if (typeof window !== 'undefined') {
      const cachedHotelDetails = sessionStorage.getItem('cachedHotelDetails');
      if (cachedHotelDetails) {
        try {
          const parsedDetails = JSON.parse(cachedHotelDetails);
          console.log('Using cached hotel details after error');
          return parsedDetails;
        } catch (cacheError) {
          console.warn('Error parsing cached hotel details:', cacheError);
        }
      }
    }
    
    // Return null instead of hardcoded fallback data
    console.log('Hotel details fetch failed, returning null');
    return null;
  }
};

export const createCustomer = async (customerData) => {
  try {
    const myHeaders = getJsonHeaders();
    
    // Extract property_code from the request data
    const propertyCode = customerData.property_code;
    const customerRequestData = { ...customerData };
    delete customerRequestData.property_code; // Remove from request body
    
    // Require property_code, throw error if missing
    if (!propertyCode) {
      throw new Error('Property code is required for customer creation');
    }
    
    // Override X-Database header with the property code
    myHeaders.set('X-Database', propertyCode);
    console.log('Using property code for customer creation:', propertyCode);
    console.log('Updated headers for customer request:', Object.fromEntries(myHeaders.entries()));

    const response = await fetch(`${BASE_URL}/customers`, {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify(customerRequestData),
      redirect: "follow"
    });

    const data = await response.json();
    console.log('Customer creation response:', data);
    return data;
  } catch (error) {
    console.error('Error creating customer:', error);
    throw error;
  }
};

export const createReservation = async (reservationData) => {
  try {
    const headers = getJsonHeaders();
    
    // Extract extra services if they exist
    const extraServices = reservationData.selectedItems || [];
    const extraServicesTotal = reservationData.extraServicesTotal || 0;
    
    // Use the amount passed from the reservation page if available
    // Otherwise calculate it based on rate and nights
    const roomTotal = reservationData.amount || 
                      (parseFloat(reservationData.rate) * parseInt(reservationData.nights));
    
    // Format extra services as notes
    let specialRequests = reservationData.special_requests || '';
    
    // Only add extra services to notes if there are any
    if (extraServices.length > 0) {
      // Format price with thousand separators
      const formatPrice = (price) => {
        return new Intl.NumberFormat('id-ID', {
          style: 'decimal',
          maximumFractionDigits: 0,
          minimumFractionDigits: 0
        }).format(price);
      };
      
      // Add a separator if there's already content in special requests
      if (specialRequests && specialRequests.trim() !== '') {
        specialRequests += '\n\n';
      }
      
      // Add header for extra services
      specialRequests += 'EXTRA SERVICES:\n';
      
      // Add each extra service as a line in the notes
      extraServices.forEach(item => {
        const price = item.amountnett || item.amountplus || 0;
        specialRequests += `- ${item.itemdesc || 'Unknown Service'}: Rp ${formatPrice(price)}\n`;
      });
      
      // Add total for extra services
      specialRequests += `\nTotal Extra Services: Rp ${formatPrice(extraServicesTotal)}`;
    }
    
    // Format payload according to API documentation
    const payload = {
      reservation_id: reservationData.reservation_id || '',
      rooms: [
        {
          room_id: parseInt(reservationData.room_id) || 0,
          room_type: reservationData.room_type || reservationData.room_info?.room_type || '',
          bedtype: reservationData.bedtype || reservationData.room_info?.bedtype || reservationData.bed_type || 'Standard',
          description: reservationData.room_info?.description || reservationData.description || '',
          number_of_rooms: parseInt(reservationData.room_count) || 1,
          include_breakfast: reservationData.breakfast_included || false,
          amount_rate: roomTotal // Add amount_rate field to match the updated API requirement
        }
      ],
      // Generate customer_id using epoch time if not provided
      customer_id: reservationData.customer_id || Math.floor(Date.now() / 1000),
      check_in_date: reservationData.check_in || '',
      check_out_date: reservationData.check_out || '',
      booker_name: reservationData.booker_name || reservationData.guest_name || '',
      booker_email: reservationData.booker_email || reservationData.guest_email || '',
      booker_phone: reservationData.booker_phone || reservationData.guest_phone || '',
      rate_code: reservationData.rate_code || '',
      note: specialRequests
    };
    
    // Add promo_code to payload if provided
    if (reservationData.promo_code) {
      payload.promo_code = reservationData.promo_code;
      console.log(`✅ Adding promo_code to reservation payload: ${reservationData.promo_code}`);
    } else {
      console.log('ℹ️ No promo_code provided in reservationData');
    }
    
    // Add member_id to payload if provided
    if (reservationData.member_id) {
      payload.member_id = reservationData.member_id;
      console.log(`✅ Adding member_id to reservation payload: ${reservationData.member_id}`);
    } else {
      console.log('ℹ️ No member_id provided in reservationData');
    }
    
    // Add point redemption data to payload if using points
    if (reservationData.use_point || reservationData.points_use) {
      payload.use_point = reservationData.use_point ? 1 : 0;
      if (reservationData.points_use) {
        payload.points_use = reservationData.points_use;
      }
      console.log(`✅ Adding point redemption to reservation payload: use_point=${payload.use_point}, points_use=${payload.points_use}`);
    } else {
      console.log('ℹ️ No point redemption provided in reservationData');
    }
    
    // Log to verify the amount_rate field
    console.log(`API payload for reservation ${payload.reservation_id} includes amount_rate:`, 
                payload.rooms[0].amount_rate);
    
    // Check for missing required fields
    const requiredFields = [
      'reservation_id', 'booker_name', 'booker_email', 'booker_phone',
      'check_in_date', 'check_out_date', 'rate_code'
    ];
    
    const missingFields = requiredFields.filter(field => !payload[field] && payload[field] !== 0);
    
    // Validate room_id separately since it's nested
    if (!payload.rooms[0].room_id && payload.rooms[0].room_id !== 0) {
      missingFields.push('room_id');
    }
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required fields for reservation: ${missingFields.join(', ')}`);
    }
    
    console.log('Creating reservation with payload:', JSON.stringify(payload, null, 2));
    console.log('Headers:', Object.fromEntries(headers.entries()));
    
    const response = await fetch(`${BASE_URL}/reservations`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      // Try to get more information about the error
      let errorMessage = `Failed to create reservation: ${response.status}`;
      try {
        const errorData = await response.text();
        console.error('Error response body:', errorData);
        
        try {
          // Try to parse as JSON
          const jsonError = JSON.parse(errorData);
          if (jsonError.message) {
            errorMessage = `Failed to create reservation: ${response.status} - ${jsonError.message}`;
          } else if (jsonError.error) {
            errorMessage = `Failed to create reservation: ${response.status} - ${jsonError.error}`;
          }
          console.error('Parsed error data:', jsonError);
        } catch (parseError) {
          // If not JSON, use as text
          if (errorData) {
            errorMessage = `Failed to create reservation: ${response.status} - ${errorData}`;
          }
        }
      } catch (textError) {
        console.error('Could not read error response body:', textError);
      }
      
      throw new Error(errorMessage);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating reservation:', error);
    throw error;
  }
};

export const createPayment = async (paymentData) => {
  try {
    // Create a copy of the payment data to modify
    const paymentRequestData = { ...paymentData };
    
    // Extract property_code from the request data
    const propertyCode = paymentRequestData.property_code;
    delete paymentRequestData.property_code; // Remove from request body
    
    // Require property_code, throw error if missing
    if (!propertyCode) {
      throw new Error('Property code is required for payment creation');
    }
    
    // Simpan propertyCode di localStorage untuk digunakan getHeaders()
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('selectedProperty', JSON.stringify({
          code: propertyCode,
          description: propertyCode
        }));
        console.log('Property code set in localStorage for payment:', propertyCode);
      } catch (err) {
        console.error('Error storing property code in localStorage:', err);
      }
    }
    
    // Dapatkan header baru setelah menyimpan propertyCode
    const myHeaders = getJsonHeaders();
    console.log('Headers for payment request:', Object.fromEntries(myHeaders.entries()));
    
    // Ensure paymentgate is included in the request and is uppercase
    if (!paymentRequestData.paymentgate) {
      console.error('Payment gateway not specified in request');
      throw new Error('Payment gateway is required for payment processing');
    } else {
      // Ensure payment gateway is uppercase if it's a string, unless it's paylabs which prefers lowercase
      if (typeof paymentRequestData.paymentgate === 'string') {
        if (paymentRequestData.paymentgate.toLowerCase() === 'paylabs') {
          paymentRequestData.paymentgate = 'paylabs';
        } else {
          paymentRequestData.paymentgate = paymentRequestData.paymentgate.toUpperCase();
        }
      }
      console.log(`Using payment gateway: ${paymentRequestData.paymentgate}`);
    }
    
    console.log('Payment request payload:', JSON.stringify(paymentRequestData, null, 2));

    const response = await fetch(`${BASE_URL}/payments`, {
      method: 'POST',
      headers: myHeaders,
      body: JSON.stringify(paymentRequestData),
      redirect: "follow"
    });

    // Check if response is ok
    if (!response.ok) {
      console.error('Payment API response not ok:', response.status, response.statusText);
      throw new Error(`Payment API error: ${response.status} ${response.statusText}`);
    }

    const responseText = await response.text();
    console.log('Raw payment response (first 200 chars):', responseText.substring(0, 200) + '...');
    console.log('Full payment response:', responseText);
    
    let result;
    try {
      result = JSON.parse(responseText);
      console.log('Parsed payment response:', JSON.stringify(result, null, 2));
    } catch (e) {
      console.error('Failed to parse payment response:', responseText);
      return {
        status: 'error',
        message: 'Invalid response format from payment API',
        data: responseText
      };
    }

    // Validate that the payment was actually inserted/created
    if (!result) {
      throw new Error('Empty response from payment API - payment may not have been inserted');
    }

    // Check for success status in various response formats
    if (result.status === 'error' || result.status === 'failed' || result.error) {
      const errorMsg = result.message || result.error || 'Payment insertion failed';
      console.error('Payment insertion error:', errorMsg);
      throw new Error(`Payment not inserted: ${errorMsg}`);
    }

    // Additional validation for successful payment insertion
    if (result.status === 'success' || result.data || result.token || result.redirect_url) {
      console.log('Payment successfully inserted and created:', result);
      return result;
    }

    // If we reach here, the response format is unexpected
    console.warn('Unexpected payment response format:', result);
    
    // For safety, assume payment failed if we can't confirm success
    throw new Error('Cannot confirm payment insertion - unexpected response format');
  } catch (error) {
    console.error('Error creating payment:', error);
    // Re-throw with more context
    throw new Error(`Payment insertion failed: ${error.message}`);
  }
};

export const generateReservationId = async () => {
  try {
    const response = await makeApiRequest(`${BASE_URL}/reservations/generate-id`, {
      method: 'GET',
      headers: getHeaders()
    });
    
    return response;
  } catch (error) {
    console.error('Error generating reservation ID:', error);
    return { status: 'error', message: 'Failed to generate reservation ID' };
  }
};

// Get member points by phone number
export const getMemberPoints = async (phoneNumber, propertyCode) => {
  try {
    // Use PMH as default property code if not provided
    const database = propertyCode || "PMH";
    
    // Important: Use the phone number exactly as provided - no automatic formatting
    const phoneToUse = phoneNumber.trim();
    
    console.log(`Checking member points for phone: ${phoneToUse}, database: ${database}`);
    
    // Use the exact endpoint from the example
    const url = `${BASE_URL}/points/${phoneToUse}`;
    console.log(`Using points API URL: ${url}`);
    
    // Set up headers exactly as in the example
    const myHeaders = new Headers();
    myHeaders.append("X-database", database);
    
    console.log('Using headers:', {
      'X-database': database
    });
    
    const requestOptions = {
      method: "GET",
      headers: myHeaders,
      redirect: "follow"
    };
    
    console.log('Request options:', requestOptions);
    
    // Make the API call without any timeout or race condition
    try {
      const response = await fetch(url, requestOptions);
      
      // Log the raw response for debugging
      console.log(`API response status: ${response.status}, statusText: ${response.statusText}`);
      
      // Get response text
      const responseText = await response.text();
      console.log('Raw response text:', responseText);
      
      // Try to parse as JSON
      try {
        const data = JSON.parse(responseText);
        console.log('Parsed response:', data);
      
      return {
          status: data.status || 'success',
          message: data.message || 'Points retrieved successfully',
          data: data.data || ((data.remaining !== undefined || data.points !== undefined || data.guestname) ? data : null)
        };
      } catch (jsonError) {
        console.log('Error parsing JSON response:', jsonError);
        return {
          status: 'error',
          message: 'Invalid response format',
          data: null
        };
      }
    } catch (fetchError) {
      console.log('Fetch error:', fetchError);
      return {
        status: 'error',
        message: 'Connection error',
        data: null
      };
    }
  } catch (error) {
    console.log('General error:', error);
    
    return { 
      status: 'error', 
      message: 'Unable to check points',
      data: null
    };
  }
};

// Redeem points for a booking
export const redeemPoints = async (phoneNumber, redeemAmount, propertyCode) => {
  try {
    // Require property_code, throw error if missing
    if (!propertyCode) {
      throw new Error('Property code is required for redeeming points');
    }
    
    // Ensure the phone number is sanitized (using the Indonesian format)
    let sanitizedPhone = phoneNumber.trim();
    
    // If starts with 0, replace with 62
    if (sanitizedPhone.startsWith('0')) {
      sanitizedPhone = `62${sanitizedPhone.substring(1)}`;
    }
    
    // If doesn't start with 62, add it
    if (!sanitizedPhone.startsWith('62')) {
      sanitizedPhone = `62${sanitizedPhone}`;
    }

    // Simpan propertyCode di localStorage untuk digunakan getHeaders()
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('selectedProperty', JSON.stringify({
          code: propertyCode,
          description: propertyCode
        }));
        console.log('Property code set in localStorage for redeeming points:', propertyCode);
      } catch (err) {
        console.error('Error storing property code in localStorage:', err);
      }
    }
    
    // Dapatkan header baru setelah menyimpan propertyCode
    const headers = getJsonHeaders();
    console.log('Headers for redeeming points request:', Object.fromEntries(headers.entries()));

    const response = await makeApiRequest(`${BASE_URL}/points/${sanitizedPhone}`, {
      method: 'PUT',
      headers: headers,
      body: JSON.stringify({ redeem: redeemAmount })
    });
    
    return response;
  } catch (error) {
    console.error('Error redeeming points:', error);
    return { 
      status: 'error', 
      message: error.message || 'Failed to redeem points',
      data: null
    };
  }
};

// New function for redeem point API endpoint
export const redeemPointsAPI = async (mbrno, pointRedeem) => {
  try {
    // Ensure the phone number is sanitized (using the Indonesian format)
    let sanitizedPhone = mbrno.trim();
    
    // If starts with 0, replace with 62
    if (sanitizedPhone.startsWith('0')) {
      sanitizedPhone = `62${sanitizedPhone.substring(1)}`;
    }
    
    // If doesn't start with 62, add it
    if (!sanitizedPhone.startsWith('62')) {
      sanitizedPhone = `62${sanitizedPhone}`;
    }
    
    const headers = getJsonHeaders();
    console.log('Headers for redeem point API request:', Object.fromEntries(headers.entries()));

    // Step 1: Insert redeem point record
    const insertResponse = await makeApiRequest(`${BASE_URL}/redeem-point`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({ 
        mbrno: sanitizedPhone,
        point_redeem: pointRedeem
      })
    });
    
    console.log('Redeem point insertion response:', insertResponse);
    
    // Step 2: Update points balance using the PUT endpoint
    try {
      const updateHeaders = getJsonHeaders();
      console.log('Headers for updating points balance:', Object.fromEntries(updateHeaders.entries()));
      
      const updateResponse = await makeApiRequest(`${BASE_URL}/points/${sanitizedPhone}`, {
        method: 'PUT',
        headers: updateHeaders,
        body: JSON.stringify({ 
          redeem: pointRedeem 
        })
      });
      
      console.log('Points balance update response:', updateResponse);
      
      // If update was successful, return that response as it contains the updated balance
      if (updateResponse && updateResponse.status === 'success') {
        return updateResponse;
      }
    } catch (updateError) {
      console.error('Error updating points balance:', updateError);
      // Continue with the original response even if update fails
    }
    
    // Return the original insertion response if update failed or wasn't available
    return insertResponse;
  } catch (error) {
    console.error('Error redeeming points via API:', error);
    return { 
      status: 'error', 
      message: error.message || 'Failed to redeem points',
      data: null
    };
  }
};

export const searchRooms = async ({ checkIn, checkOut, adults, children, rooms, propertyCode = null }) => {
  try {
    let url = `${BASE_URL}/rooms/search?checkIn=${checkIn}&checkOut=${checkOut}&adults=${adults}&children=${children}&rooms=${rooms}`;
    if (propertyCode) {
      url += `&property=${propertyCode}`;
    }

    const myHeaders = getHeaders();

    const response = await fetch(url, {
      method: 'GET',
      headers: myHeaders,
      redirect: 'follow'
    });

    if (!response.ok) {
      throw new Error('Failed to fetch rooms');
    }

    return await response.json();
  } catch (error) {
    console.error('Error searching rooms:', error);
    throw error;
  }
};

export const fetchHotelFacilities = async () => {
  try {
    const myHeaders = getHeaders();

    const response = await fetch(`${BASE_URL}/hotel_facility`, {
      method: "GET",
      headers: myHeaders,
      redirect: "follow"
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Hotel facilities:', data); 
    return data;
  } catch (error) {
    console.error('Error fetching hotel facilities:', error);
    throw error;
  }
};

export const fetchPromotion = async () => {
  try {
    const myHeaders = getHeaders();

    const response = await fetch(`${BASE_URL}/alert_promotion`, {
      headers: myHeaders,
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching promotion:', error);
    return null;
  }
};

export const fetchRoomRates = async (startDate, endDate, propertyCode) => {
  try {
    // Require property_code, throw error if missing
    if (!propertyCode) {
      throw new Error('Property code is required for fetching room rates');
    }
    
    const myHeaders = getHeaders();
    
    // Override X-Database header with the property code
    myHeaders.set('X-Database', propertyCode);
    console.log(`Setting X-Database header to: ${propertyCode} for room rates`);

    // Ubah endpoint sesuai dengan API yang tersedia
    const response = await fetch(`${BASE_URL}/rooms/rates?fromdate=${startDate}&todate=${endDate}`, {
      method: "GET",
      headers: myHeaders,
      redirect: "follow"
    });

    // Return default data jika API belum tersedia
    return {
      status: 'success',
      data: [
        {
          date: startDate,
          base_rate: 1739720
        },
        {
          date: endDate,
          base_rate: 1811020
        }
      ]
    };

  } catch (error) {
    console.error('Error fetching room rates:', error);
    // Return default data on error
    return {
      status: 'success',
      data: [
        {
          date: startDate,
          base_rate: 1739720
        }
      ]
    };
  }
};

// Helper function to test the API directly
export const testRoomFilterAPI = (checkIn = '2025-03-01', checkOut = '2025-03-02', adults = '2', children = '0', room = '1', roomtype = '', ratecode = '', propertyCode = null) => {
  // Determine which endpoint to use based on whether filtering is needed
  const endpoint = (roomtype || ratecode) ? 'rooms/filtered' : 'rooms/available';

  let url = `${BASE_URL}/${endpoint}?checkIn=${checkIn}&checkOut=${checkOut}&adults=${adults}&children=${children}&room=${room}`;
  if (propertyCode) {
    url += `&property=${propertyCode}`;
  }

  // Add roomType and rateCode parameters if provided
  if (roomtype) {
    url += `&roomtype=${encodeURIComponent(roomtype)}`;
  }

  if (ratecode) {
    url += `&ratecode=${encodeURIComponent(ratecode)}`;
  }

  console.log(`Testing API with URL: ${url}`);

  return makeApiRequest(url, {
    method: 'GET',
    redirect: 'follow'
  }, 8000).then(result => {
    console.log("API Test Result:", result);
    return result;
  });
};

// Mock data for promotions
export const mockPromotionData = () => {
  return {
    status: 'success',
    message: 'Promotions fetched successfully',
    data: [
      {
        id: 1,
        title: 'Weekend Getaway Special',
        description: 'Enjoy a luxurious weekend stay with 20% off our standard rates. Includes breakfast for two.',
        image: '/images/promo1.jpg',
        button_desc: 'Book Now',
        url_destination: '/promo',
        active: true,
        display_pages: ['home', 'booking'],
        priority: 1,
        start_date: '2025-01-01',
        end_date: '2025-12-31',
        roomtype: 'Standard',
        ratecode: 'WEEKEND'
      },
      {
        id: 2,
        title: 'Summer Family Package',
        description: 'Perfect for family vacations! Kids stay free and enjoy complimentary access to our pool and kids club.',
        image: '/images/promo2.jpg',
        button_desc: 'View Details',
        url_destination: '/promo',
        active: true,
        display_pages: ['home'],
        priority: 2,
        start_date: '2025-06-01',
        end_date: '2025-08-31',
        roomtype: 'Family',
        ratecode: 'SUMMER'
      },
      {
        id: 3,
        title: 'Business Travel Offer',
        description: 'Special rates for business travelers. Includes high-speed WiFi, breakfast, and late checkout.',
        image: '/images/promo3.jpg',
        button_desc: 'Reserve Now',
        url_destination: '/promo',
        active: true,
        display_pages: ['booking'],
        priority: 3,
        start_date: '2025-01-01',
        end_date: '2025-12-31',
        roomtype: 'Deluxe',
        ratecode: 'BUSINESS'
      }
    ]
  };
};

export const fetchHotelFavicon = async () => {
  try {
    const headers = getHeaders();
    console.log('Fetching favicon with headers:', Object.fromEntries(headers.entries()));

    const response = await fetch(`${BASE_URL}/hotels/1`, {
      method: 'GET',
      headers: headers,
      redirect: 'follow',
      cache: 'no-store'
    });

    if (!response.ok) {
      console.error('Favicon fetch failed with status:', response.status);
      return null;
    }

    const data = await response.json();
    console.log('Received hotel data:', {
      status: data.status,
      hasLogo: !!data.data?.logo,
      logoDataLength: data.data?.logo?.data?.length
    });

    if (data.status === 'success' && data.data?.logo?.data) {
      try {
        // Convert uint8 array to base64
        const uint8Array = new Uint8Array(data.data.logo.data);
        console.log('Logo data length:', uint8Array.length);

        // In client-side code, we can't write to the filesystem directly
        // Instead, we'll return the data for the layout.js to handle
        return data.data.logo.data;
      } catch (error) {
        console.error('Error converting logo data:', error);
        return null;
      }
    } else {
      console.warn('No logo data found in API response');
      return null;
    }
  } catch (error) {
    console.error('Error in fetchHotelFavicon:', error);
    return null;
  }
};

export { multiproperty, propertycode };

// Add CHECKIN_CONFIG to exports
export { CHECKIN_CONFIG };

// Fetch hotel details with payment gateway information
export const fetchHotelPaymentGate = async (propertyCode) => {
  try {
    // Require property_code, throw error if missing
    if (!propertyCode) {
      throw new Error('Property code is required for fetching hotel payment gateway');
    }
    
    console.log(`Fetching hotel payment gateway for property: ${propertyCode}`);
    
    const headers = getHeaders();
    
    // Override X-Database header with the property code
    headers.set('X-Database', propertyCode);
    console.log(`Setting X-Database header to: ${propertyCode} for payment gateway fetch`);
    
    const response = await fetch(`${BASE_URL}/hotels/1`, {
      method: "GET",
      headers: headers,
      redirect: "follow"
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    // Get the raw text first for debugging
    const responseText = await response.text();
    console.log('Raw API response (first 100 chars):', responseText.substring(0, 100) + '...');
    
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (parseError) {
      console.error('Error parsing hotel details response:', parseError);
      throw new Error(`Failed to parse API response: ${parseError.message}`);
    }
    
    // Log the complete response structure to help with debugging
    console.log('Hotel payment gateway data structure:', {
      status: data.status,
      hasData: !!data.data,
      paymentgate: data.paymentgate || data.data?.paymentgate,
      hasApiKey: !!(data.apikey || data.data?.apikey),
      responseKeys: Object.keys(data),
      dataKeys: data.data ? Object.keys(data.data) : []
    });
    
    // Ensure we have a payment gateway value
    const paymentGate = data.paymentgate || data.data?.paymentgate;
    
    if (!paymentGate) {
      console.error('Payment gateway not found in API response');
      console.log('Full API response:', JSON.stringify(data, null, 2));
      throw new Error(`Payment gateway configuration not found for property ${propertyCode}`);
    }
    
    console.log(`Found payment gateway for property ${propertyCode}: ${paymentGate}`);
    
    // Ensure the paymentgate is directly accessible in the returned object
    return {
      ...data,
      paymentgate: paymentGate
    };
  } catch (error) {
    console.error('Error fetching hotel payment gateway:', error);
    throw error;
  }
};

// Fetch upselling items
export const fetchUpsellItems = async (propertyCode) => {
  try {
    const headers = new Headers({
      'Content-Type': 'application/json'
    });
    
    if (propertyCode) {
      headers.append('X-Database', propertyCode);
    }
    
    const response = await fetch(`${BASE_URL}/items`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch upsell items: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching upsell items:', error);
    return { status: 'error', message: error.message, data: [] };
  }
};

// Send OTP to phone number
export const sendOTP = async (phoneNumber, propertyCode) => {
  try {
    if (!phoneNumber) {
      throw new Error('Phone number is required');
    }

    if (!propertyCode) {
      throw new Error('Property code is required');
    }

    console.log(`Sending OTP to phone: ${phoneNumber}, property: ${propertyCode}`);

    // Generate 6-digit OTP code
    const otpCode = Math.floor(100000 + Math.random() * 900000).toString();
    
    // Set expiry time to 10 minutes from now in Asia/Jakarta timezone (UTC+7)
    const now = new Date();
    const expiryTime = new Date(now.getTime() + 10 * 60000); // Add 10 minutes
    
    // Convert to Asia/Jakarta timezone (UTC+7)
    const jakartaTime = new Date(expiryTime.toLocaleString('en-US', { timeZone: 'Asia/Jakarta' }));
    
    // Format as YYYY-MM-DD HH:MM:SS
    const year = jakartaTime.getFullYear();
    const month = String(jakartaTime.getMonth() + 1).padStart(2, '0');
    const day = String(jakartaTime.getDate()).padStart(2, '0');
    const hours = String(jakartaTime.getHours()).padStart(2, '0');
    const minutes = String(jakartaTime.getMinutes()).padStart(2, '0');
    const seconds = String(jakartaTime.getSeconds()).padStart(2, '0');
    
    const expiresAt = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    
    console.log('Current time (Jakarta):', new Date().toLocaleString('en-US', { timeZone: 'Asia/Jakarta' }));
    console.log('OTP will expire at (Jakarta):', expiresAt);

    // Use new OTP API endpoint
    const url = `${OTP_BASE_URL}/otp`;
    console.log(`Using new OTP API endpoint: ${url}`);
    
    const headers = getHeaders();
    // Override the X-Database header with the provided property code
    headers.set('X-Database', propertyCode);

    // Create a timeout promise that rejects after 8 seconds
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('OTP request timed out')), 8000)
    );
    
    // Race the API request against the timeout
    const result = await Promise.race([
      makeApiRequest(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ 
          phone: phoneNumber,
          code: otpCode,
          expires_at: expiresAt
        })
      }),
      timeoutPromise
    ]);

    console.log('OTP send result:', result);
    
    // Check if API returned success
    if (result && (result.status === 'success' || result.message === 'OTP code created successfully')) {
      console.log('OTP created successfully in database');
      console.log('🔐 OTP CODE (for testing):', otpCode);
      console.log('⏰ Expires at:', expiresAt);
      return {
        success: true,
        message: result.message || "OTP sent successfully",
        data: {
          expires: result.expires_at || expiresAt,
          code: otpCode // Store code for verification (for testing purposes)
        }
      };
    }
    
    // If the API is down but we need to continue for demo purposes,
    // return a mock successful response
    if (result.status === 'error' && process.env.NODE_ENV === 'development') {
      console.warn('API error in development mode, returning mock OTP response');
      return {
        success: true,
        message: "OTP sent successfully (MOCK)",
        data: {
          expires: expiresAt,
          code: otpCode
        }
      };
    }
    
    // If result has error status, return it as is
    if (result.status === 'error') {
      return {
        success: false,
        message: result.message || 'Failed to send OTP'
      };
    }
    
    return result;
  } catch (error) {
    console.error('Error sending OTP:', error);
    
    // In development mode, return a mock response to allow testing
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error in development mode, returning mock OTP response');
      const otpCode = Math.floor(100000 + Math.random() * 900000).toString();
      
      // Use Jakarta timezone for mock response too
      const expiryTime = new Date(Date.now() + 10 * 60000);
      const jakartaTime = new Date(expiryTime.toLocaleString('en-US', { timeZone: 'Asia/Jakarta' }));
      const year = jakartaTime.getFullYear();
      const month = String(jakartaTime.getMonth() + 1).padStart(2, '0');
      const day = String(jakartaTime.getDate()).padStart(2, '0');
      const hours = String(jakartaTime.getHours()).padStart(2, '0');
      const minutes = String(jakartaTime.getMinutes()).padStart(2, '0');
      const seconds = String(jakartaTime.getSeconds()).padStart(2, '0');
      const expiresAt = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      
      return {
        success: true,
        message: "OTP sent successfully (MOCK)",
        data: {
          expires: expiresAt,
          code: otpCode
        }
      };
    }
    
    return {
      success: false,
      status: 'error',
      message: error.message || 'Failed to send OTP'
    };
  }
};

// Verify OTP code
export const verifyOTP = async (phoneNumber, otpCode, propertyCode) => {
  try {
    if (!phoneNumber || !otpCode) {
      throw new Error('Phone number and OTP code are required');
    }

    if (!propertyCode) {
      throw new Error('Property code is required');
    }

    console.log(`Verifying OTP for phone: ${phoneNumber}, property: ${propertyCode}`);

    // Use new OTP verification endpoint
    const url = `${OTP_BASE_URL}/otp/verify`;
    console.log(`Using OTP verification endpoint: ${url}`);
    
    const headers = getHeaders();
    // Override the X-Database header with the provided property code
    headers.set('X-Database', propertyCode);

    // Create a timeout promise that rejects after 8 seconds
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('OTP verification timed out')), 8000)
    );
    
    // Race the API request against the timeout
    const result = await Promise.race([
      makeApiRequest(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ 
          phone: phoneNumber,
          code: otpCode
        })
      }),
      timeoutPromise
    ]);

    console.log('OTP verification result:', result);
    
    // Check if verification was successful
    if (result && result.status === 'success') {
      console.log('✅ OTP verified successfully and marked as used');
      console.log('Time remaining:', result.time_remaining);
      console.log('Note:', result.note);
      return {
        success: true,
        message: result.message || 'OTP code verified successfully and marked as used',
        data: {
          phone: result.phone,
          verified_at: result.verified_at,
          expires_at: result.expires_at,
          created_at: result.created_at,
          time_remaining: result.time_remaining,
          note: result.note
        }
      };
    }
    
    // Handle specific error cases
    if (result && result.status === 'error') {
      // 1. Check for INVALID OTP (not found in database)
      if (result.message && result.message.toLowerCase().includes('invalid')) {
        console.warn('❌ OTP is invalid:', result.message);
        return {
          success: false,
          errorType: 'INVALID',
          message: result.message || 'Invalid OTP code - phone and code combination not found'
        };
      }
      
      // 2. Check for EXPIRED OTP (current_time > expires_at)
      if (result.message && result.message.toLowerCase().includes('expired')) {
        console.warn('❌ OTP has expired:', result.message);
        return {
          success: false,
          errorType: 'EXPIRED',
          message: result.message || 'OTP code has expired - time limit reached',
          expired_at: result.expired_at,
          current_time: result.current_time,
          created_at: result.created_at,
          send_status: result.send_status,
          usage_status: result.usage_status,
          explanation: result.explanation || 'OTP codes have a time limit and cannot be used after expiration'
        };
      }
      
      // 3. Check for NOT SENT BY SERVICE (is_send = 0)
      if (result.message && result.message.toLowerCase().includes('not been sent by service')) {
        console.warn('📧 OTP not sent by service yet:', result.message);
        return {
          success: false,
          errorType: 'NOT_SENT',
          message: result.message || 'OTP code has not been sent by service yet',
          created_at: result.created_at,
          expires_at: result.expires_at,
          explanation: result.explanation || 'Service needs to send this OTP first before it can be verified',
          note: result.note || 'is_send = 0 means not yet sent by service, is_send = 1 means sent and ready to verify'
        };
      }
      
      // 4. Check for ALREADY USED (is_used = 1)
      if (result.message && result.message.toLowerCase().includes('already been used')) {
        console.warn('🔒 OTP already used:', result.message);
        return {
          success: false,
          errorType: 'ALREADY_USED',
          message: result.message || 'OTP code has already been used',
          created_at: result.created_at,
          expires_at: result.expires_at,
          explanation: result.explanation || 'This OTP code has already been verified and used',
          note: result.note || 'is_used = 1 means already verified/used, is_used = 0 means ready to verify'
        };
      }
      
      // Generic error
      return {
        success: false,
        errorType: 'UNKNOWN',
        message: result.message || 'Failed to verify OTP'
      };
    }
    
    // In development mode, accept any 6-digit code as valid
    if (process.env.NODE_ENV === 'development') {
      console.warn('API error in development mode, checking mock OTP verification');
      // Accept any 6-digit code in development mode
      if (otpCode && otpCode.length === 6 && /^\d+$/.test(otpCode)) {
        return {
          success: true,
          message: "OTP verified successfully (MOCK)"
        };
      }
    }
    
    return result;
  } catch (error) {
    console.error('Error verifying OTP:', error);
    
    // In development mode, accept any 6-digit code as valid
    if (process.env.NODE_ENV === 'development') {
      console.warn('Error in development mode, checking mock OTP verification');
      if (otpCode && otpCode.length === 6 && /^\d+$/.test(otpCode)) {
        return {
          success: true,
          message: "OTP verified successfully (MOCK)"
        };
      }
    }
    
    return {
      success: false,
      status: 'error',
      message: error.message || 'Failed to verify OTP'
    };
  }
};


// New function to handle multiple rooms in a single reservation
export const createMultiRoomReservation = async (reservationData, roomsData) => {
  try {
    console.log('=== createMultiRoomReservation called ===');
    console.log('reservationData:', JSON.stringify(reservationData, null, 2));
    console.log('member_id in reservationData:', reservationData.member_id);
    console.log('promo_code in reservationData:', reservationData.promo_code);
    
    const headers = getJsonHeaders();
    
    // Extract property code from reservationData
    const propertyCode = reservationData.property_code;
    if (propertyCode) {
      headers.set('X-Database', propertyCode);
    }
    
    // Format special requests
    let specialRequests = reservationData.specialRequests || '';
    
    // Prepare an array to hold individual reservation promises
    const reservationPromises = [];
    
    // Process each room as a separate reservation with a unique reservation_id
    roomsData.forEach((room, index) => {
      // Create unique reservation ID for each room by appending index
      const uniqueReservationId = `${reservationData.reservation_id}-${index + 1}`;
      
      // Calculate room total amount
      const roomTotal = room.amount || 
                        (parseFloat(room.rate) * parseInt(reservationData.nights));
      
      // Process extra services for this room if any
      if (room.selectedServices && room.selectedServices.length > 0) {
        const formatPrice = (price) => {
          return new Intl.NumberFormat('id-ID', {
            style: 'decimal',
            maximumFractionDigits: 0,
            minimumFractionDigits: 0
          }).format(price);
        };
        
        // Add room info to special requests
        if (specialRequests && specialRequests.trim() !== '') {
          specialRequests += '\n\n';
        }
        specialRequests += `EXTRA SERVICES FOR ${room.room_info?.room_type || 'ROOM'}:\n`;
        
        // Add each extra service
        room.selectedServices.forEach(service => {
          const price = service.amountnett || service.amountplus || 0;
          specialRequests += `- ${service.itemdesc || 'Unknown Service'}: Rp ${formatPrice(price)}\n`;
        });
        
        // Add total for this room's extra services
        const roomServicesTotal = room.selectedServices.reduce((total, service) => {
          return total + (service.amountnett || service.amountplus || 0);
        }, 0);
        
        specialRequests += `Total Extra Services for this room: Rp ${formatPrice(roomServicesTotal)}\n`;
      }
      
      // Prepare individual room reservation payload
      const payload = {
        reservation_id: uniqueReservationId,
        rooms: [
          {
            room_id: parseInt(room.room_info?.room_id) || 0,
            room_type: room.room_info?.room_type || room.room_type || '',
            bedtype: room.room_info?.bedtype || room.bedtype || room.bed_type || 'Standard',
            description: room.room_info?.description || room.description || '',
            number_of_rooms: 1,
            include_breakfast: room.rate_details?.include_breakfast || false,
            amount_rate: roomTotal
          }
        ],
        customer_id: reservationData.customer_id,
        check_in_date: reservationData.check_in_date,
        check_out_date: reservationData.check_out_date,
        booker_name: reservationData.booker_name,
        booker_email: reservationData.booker_email,
        booker_phone: reservationData.booker_phone,
        rate_code: room.rate_details?.rate_code || reservationData.rate_code,
        note: specialRequests
      };
      
      // Add promo_code to payload if provided
      if (reservationData.promo_code) {
        payload.promo_code = reservationData.promo_code;
        console.log(`✅ Adding promo_code to multi-room reservation payload: ${reservationData.promo_code}`);
      } else {
        console.log('ℹ️ No promo_code provided in reservationData');
      }
      
      // Add member_id to payload if provided
      if (reservationData.member_id) {
        payload.member_id = reservationData.member_id;
        console.log(`✅ Adding member_id to multi-room reservation payload: ${reservationData.member_id}`);
      } else {
        console.log('ℹ️ No member_id provided in reservationData');
      }
      
      // Add point redemption data to payload if using points
      // CRITICAL: points_use must be the ROOM'S PROPORTIONAL SHARE (room.points_used),
      // NOT the full total - otherwise the backend deducts/discounts points N times
      // for an N-room booking (over-discount + over-deduction).
      const roomPoints = room.points_used && room.points_used > 0 ? room.points_used : 0;
      if (reservationData.use_point || reservationData.points_use) {
        if (roomPoints > 0) {
          payload.use_point = 1;
          payload.points_use = roomPoints;
        }
        console.log(`✅ Adding point redemption to multi-room reservation payload: use_point=${payload.use_point || 0}, points_use=${payload.points_use || 0} (room share of ${reservationData.points_use || 0})`);
      } else {
        console.log('ℹ️ No point redemption provided in reservationData');
      }
      
      console.log(`Creating reservation for room ${index + 1} with ID: ${uniqueReservationId}`);
      console.log('Payload for room:', JSON.stringify(payload, null, 2));
      
      // Create promise for this reservation
      const reservationPromise = fetch(`${BASE_URL}/reservations`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      }).then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            throw new Error(`Failed to create reservation for room ${index + 1}: ${response.status} - ${text}`);
          });
        }
        return response.json();
      });
      
      // Add to promises array
      reservationPromises.push(reservationPromise);
    });
    
    // Wait for all reservations to complete
    const results = await Promise.all(reservationPromises);
    console.log(`All ${results.length} room reservations created successfully`);
    console.log('Multiple room reservation summary:');
    console.log(`- Main reservation ID: ${reservationData.reservation_id}`);
    console.log(`- Individual reservation IDs: ${roomsData.map((_, index) => `${reservationData.reservation_id}-${index + 1}`).join(', ')}`);
    console.log(`- Customer ID: ${reservationData.customer_id}`);
    console.log(`- Property code: ${reservationData.property_code}`);
    console.log(`- Member ID: ${reservationData.member_id || 'not provided'}`);
    console.log(`- Promo Code: ${reservationData.promo_code || 'not provided'}`);
    
    // Return the results with the main reservation ID
    return {
      status: 'success',
      message: `Successfully created ${results.length} room reservations`,
      data: results,
      reservation_id: reservationData.reservation_id,
      individual_reservation_ids: roomsData.map((_, index) => `${reservationData.reservation_id}-${index + 1}`)
    };
  } catch (error) {
    console.error('Error creating multi-room reservation:', error);
    throw error;
  }
};

/**
 * Update room availability after successful reservation
 * Reduces the rooms_to_sell count for each booked room
 * @param {Array} roomsData - Array of room objects with room_id, check-in, check-out dates
 * @param {string} propertyCode - Property code for the hotel
 * @returns {Promise} - Result of the availability update
 */
export const updateRoomAvailabilityAfterBooking = async (roomsData, propertyCode, checkInDate, checkOutDate) => {
  try {
    if (!propertyCode) {
      throw new Error('Property code is required for updating room availability');
    }

    if (!roomsData || roomsData.length === 0) {
      throw new Error('No room data provided for availability update');
    }

    console.log('🔄 Updating room availability after booking confirmation...');
    console.log(`Property: ${propertyCode}`);
    console.log(`Rooms to update: ${roomsData.length}`);
    console.log(`Date range: ${checkInDate} to ${checkOutDate}`);

    // Prepare headers with property code
    const myHeaders = new Headers();
    myHeaders.append("X-Database", propertyCode);
    myHeaders.append("x-property", propertyCode);
    myHeaders.append("Content-Type", "application/json");

    // Process each room to reduce availability
    // Group rooms by ID to send aggregated updates
    const roomCounts = {};
    roomsData.forEach(room => {
      const roomId = room.room_info?.room_id || room.room_id;
      if (roomId) {
        roomCounts[roomId] = (roomCounts[roomId] || 0) + 1;
      }
    });

    const uniqueRoomIds = Object.keys(roomCounts);
    console.log(`Aggregated updates for ${uniqueRoomIds.length} unique rooms`);

    const updatePromises = uniqueRoomIds.map(async (roomId) => {
      const reductionAmount = roomCounts[roomId];
      
      console.log(`📉 Reducing availability for Room ID ${roomId} by ${reductionAmount}`);
      
      let successfulUpdate = false;
      let errors = [];
      let lastResult = null;

      try {
        // 1. Fetch current availability to get absolute values
        // We use 1 adult, 0 children, 1 room just to get the data
        console.log(`  - Fetching current availability for Room ${roomId}...`);
        const availabilityData = await fetchRoomAvailability(
          checkInDate, 
          checkOutDate, 
          1, 
          0, 
          1, 
          '', 
          '', 
          propertyCode
        );
        
        if (!availabilityData || !availabilityData.data) {
          throw new Error('Failed to fetch current availability data');
        }
        
        // Find the specific room in the response
        // Note: room_id in response might be number or string
        const roomData = availabilityData.data.find(r => 
          String(r.room_info?.room_id) === String(roomId) || 
          String(r.room_id) === String(roomId)
        );
        
        if (!roomData) {
          throw new Error(`Room ID ${roomId} not found in availability data`);
        }
        
        // 2. Prepare daily updates with absolute values
        const dailyUpdates = [];
        
        // Helper to generate date range
        const getDatesInRange = (startDate, endDate) => {
          const date = new Date(startDate);
          const end = new Date(endDate);
          const dates = [];
          while (date < end) {
            dates.push(new Date(date).toISOString().split('T')[0]);
            date.setDate(date.getDate() + 1);
          }
          return dates;
        };
        
        const targetDates = getDatesInRange(checkInDate, checkOutDate);
        const sourceDates = roomData.availability?.dates || [];
        
        targetDates.forEach(dateStr => {
          // Find current availability for this date
          const dayData = sourceDates.find(d => d.date === dateStr);
          let currentStock = 0;
          
          if (dayData && (dayData.rooms_to_sell !== undefined)) {
             currentStock = parseInt(dayData.rooms_to_sell);
          } else if (roomData.availability?.rooms_to_sell !== undefined) {
             // Fallback to general availability if daily not found
             currentStock = parseInt(roomData.availability.rooms_to_sell);
          }
          
          // Calculate new stock (ensure not negative)
          const newStock = Math.max(0, currentStock - reductionAmount);
          
          console.log(`  - Date ${dateStr}: Current ${currentStock} - ${reductionAmount} = ${newStock}`);
          
          dailyUpdates.push({
            start_date: dateStr,
            end_date: dateStr, // Update specific day
            minimumstay: "1",
            maximumstay: "7",
            closed: "0",
            rooms_to_sell: String(newStock) // Set absolute value
          });
        });

        if (dailyUpdates.length === 0) {
           throw new Error('No dates to update');
        }

        // 3. Send update request with all daily updates
        const payload = {
          raterequest: {
            room: [
              {
                room_id: String(roomId),
                rate: {
                  dates: dailyUpdates
                }
              }
            ]
          }
        };

        console.log(`  - Sending update for Room ${roomId} with payload:`, JSON.stringify(payload));

        const response = await fetch('/api/proxy-pms/pms/update-availability', {
          method: 'POST',
          headers: myHeaders,
          body: JSON.stringify(payload),
          redirect: 'follow'
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`❌ Update failed for Room ID ${roomId}:`, errorText);
          errors.push(`HTTP ${response.status} - ${errorText}`);
        } else {
          const result = await response.json();
          lastResult = result;
          successfulUpdate = true;
          console.log(`  ✅ Update succeeded for Room ID ${roomId}`);
        }
      } catch (error) {
        console.error(`❌ Error updating Room ID ${roomId}:`, error);
        errors.push(error.message);
      }

      return {
        success: successfulUpdate,
        room_id: roomId,
        error: errors.join('; '),
        result: lastResult
      };
    });

    // Wait for all updates to complete
    const results = await Promise.all(updatePromises);
    
    // Count successes and failures
    const successCount = results.filter(r => r.success).length;
    const failureCount = results.filter(r => !r.success).length;

    console.log('\n=== ROOM AVAILABILITY UPDATE SUMMARY ===');
    console.log(`✅ Successful unique room updates: ${successCount}/${uniqueRoomIds.length}`);
    if (failureCount > 0) {
      console.log(`❌ Failed updates: ${failureCount}/${uniqueRoomIds.length}`);
      const failedRooms = results.filter(r => !r.success);
      failedRooms.forEach(failed => {
        console.log(`  - Room ID: ${failed.room_id || 'unknown'}: ${failed.error}`);
      });
    }
    console.log('=========================================\n');

    return {
      status: successCount === uniqueRoomIds.length ? 'success' : 'partial',
      message: `Updated availability for ${successCount} out of ${uniqueRoomIds.length} unique rooms`,
      successful_updates: successCount,
      failed_updates: failureCount,
      details: results
    };

  } catch (error) {
    console.error('❌ Error in updateRoomAvailabilityAfterBooking:', error);
    return {
      status: 'error',
      message: error.message,
      successful_updates: 0,
      failed_updates: roomsData?.length || 0
    };
  }
};
